import { useContext, useEffect } from "react";

import { PubsubWebsocketContext } from "providers/PubsubWebsocketProvider";
import { useAppSelector } from "store/index";
import { selectCurrentClassUser } from "store/selectors/class-user";
import { selectUserAgentInfo } from "store/selectors/local";
import {
  selectIsMainAvTab,
  selectIsVideoSuspendedByBrowser,
} from "store/selectors/pubsub-virtual-class-state";
import { type UserAgentInfo } from "store/slices/local";
import { useObjectSelector } from "store/store-hooks";
import { useAvPubsubMessaging } from "utils/av";

type AppleMobileNavigator = Pick<Navigator, "maxTouchPoints" | "platform" | "userAgent">;

export const isAppleMobileDevice = (
  userAgentInfo: Pick<UserAgentInfo, "os"> | null,
  browserNavigator: AppleMobileNavigator,
): boolean =>
  userAgentInfo?.os === "iOS" ||
  /iPad|iPhone|iPod/.test(browserNavigator.userAgent) ||
  (browserNavigator.platform === "MacIntel" && browserNavigator.maxTouchPoints > 1);

export const useVideoSuspensionObserver = (): void => {
  const userAgentInfo = useObjectSelector(selectUserAgentInfo, ["os"]);
  const classUser = useObjectSelector(selectCurrentClassUser, ["id", "is_in_class_session"]);
  const isMainAvTab = useAppSelector(selectIsMainAvTab);
  const isVideoSuspendedByBrowser = useAppSelector((state) =>
    selectIsVideoSuspendedByBrowser(state, classUser?.id ?? ""),
  );
  const { pubsubStatus } = useContext(PubsubWebsocketContext);
  const { sendClassStateUpdate } = useAvPubsubMessaging();

  useEffect(() => {
    if (
      typeof navigator === "undefined" ||
      !isAppleMobileDevice(userAgentInfo, navigator) ||
      !classUser?.is_in_class_session ||
      !isMainAvTab ||
      pubsubStatus !== "connected"
    ) {
      return;
    }

    const updateSuspensionState = () => {
      const shouldSuspendVideo = document.visibilityState !== "visible";
      if (isVideoSuspendedByBrowser === shouldSuspendVideo) {
        return;
      }
      sendClassStateUpdate({
        isVideoSuspendedByBrowser: shouldSuspendVideo,
      });
    };

    updateSuspensionState();
    document.addEventListener("visibilitychange", updateSuspensionState);
    window.addEventListener("pageshow", updateSuspensionState);
    window.addEventListener("focus", updateSuspensionState);

    return () => {
      document.removeEventListener("visibilitychange", updateSuspensionState);
      window.removeEventListener("pageshow", updateSuspensionState);
      window.removeEventListener("focus", updateSuspensionState);
    };
  }, [
    classUser?.is_in_class_session,
    isMainAvTab,
    isVideoSuspendedByBrowser,
    pubsubStatus,
    sendClassStateUpdate,
    userAgentInfo,
  ]);
};
